/**
 * ASCII Art Generator - 动画循环系统
 *
 * 使用 requestAnimationFrame 实现可控帧率的动画循环
 * Wave 8.2: 集成性能监控
 */

import { PerformanceMonitor, type DegradeLevel, type PerformanceMonitorState } from '../export/runtime/performanceMonitor'
import { isPerfMonitorEnabled } from '../performance/features'
import type { AnimationFrame, AnimationLoopOptions } from './types'

/**
 * 动画循环控制器
 *
 * 提供帧率控制、暂停/恢复、状态查询等功能
 * Wave 8.2: 集成性能监控，支持降级策略
 */
export class AnimationLoop {
  private running = false
  private lastTime = 0
  private frameCount = 0
  private rafId: number | null = null

  private targetFPS: number
  private frameInterval: number

  private onFrame: (frame: AnimationFrame) => void
  private onPause?: () => void
  private onResume?: () => void

  // Wave 8.2: 性能监控
  private performanceMonitor: PerformanceMonitor | null = null
  private onDegrade?: (level: DegradeLevel) => void
  private onRecover?: () => void

  constructor(options: AnimationLoopOptions) {
    this.targetFPS = options.targetFPS ?? 60
    this.frameInterval = 1000 / this.targetFPS
    this.onFrame = options.onFrame
    this.onPause = options.onPause
    this.onResume = options.onResume

    // Wave 8.2: 初始化性能监控（如果启用）
    if (isPerfMonitorEnabled()) {
      this.performanceMonitor = new PerformanceMonitor({
        targetFPS: this.targetFPS,
        sampleSize: 30,
        degradeThreshold: 0.7,
        onDegrade: (level) => {
          if (this.onDegrade) {
            this.onDegrade(level)
          }
        },
        onRecover: () => {
          if (this.onRecover) {
            this.onRecover()
          }
        }
      })
    }
  }

  /**
   * 启动动画循环
   */
  start(): void {
    if (this.running) return

    this.running = true
    this.lastTime = performance.now()
    this.frameCount = 0

    if (this.onResume) {
      this.onResume()
    }

    this.rafId = requestAnimationFrame(this.tick)
  }

  /**
   * 停止动画循环
   */
  stop(): void {
    if (!this.running) return

    this.running = false

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }

    if (this.onPause) {
      this.onPause()
    }
  }

  /**
   * 切换动画状态
   */
  toggle(): void {
    if (this.running) {
      this.stop()
    } else {
      this.start()
    }
  }

  /**
   * 检查是否正在运行
   */
  isRunning(): boolean {
    return this.running
  }

  /**
   * 获取当前帧数
   */
  getFrameCount(): number {
    return this.frameCount
  }

  /**
   * 设置目标帧率
   */
  setTargetFPS(fps: number): void {
    this.targetFPS = fps
    this.frameInterval = 1000 / fps
  }

  /**
   * 获取目标帧率
   */
  getTargetFPS(): number {
    return this.targetFPS
  }

  /**
   * 内部帧处理函数
   */
  private tick = (timestamp: number): void => {
    if (!this.running) return

    const deltaTime = timestamp - this.lastTime

    // 帧率控制：只有当间隔足够时才触发回调
    if (deltaTime >= this.frameInterval) {
      // Wave 8.2: 记录帧时间到性能监控
      if (this.performanceMonitor) {
        this.performanceMonitor.recordFrame(deltaTime)
      }

      // 创建帧数据
      const frame: AnimationFrame = {
        timestamp,
        deltaTime,
        frameCount: this.frameCount++
      }

      // 调用用户回调
      this.onFrame(frame)

      // 更新上次时间，考虑累积误差
      this.lastTime = timestamp - (deltaTime % this.frameInterval)
    }

    // 继续下一帧
    this.rafId = requestAnimationFrame(this.tick)
  }

  // ============================================
  // Wave 8.2: 性能监控相关方法
  // ============================================

  /**
   * 设置降级回调
   */
  setDegradeCallback(callback: (level: DegradeLevel) => void): void {
    this.onDegrade = callback
  }

  /**
   * 设置恢复回调
   */
  setRecoverCallback(callback: () => void): void {
    this.onRecover = callback
  }

  /**
   * 获取当前FPS
   */
  getCurrentFPS(): number {
    if (this.performanceMonitor) {
      return this.performanceMonitor.getCurrentFPS()
    }
    return this.targetFPS
  }

  /**
   * 获取平均FPS
   */
  getAverageFPS(): number {
    if (this.performanceMonitor) {
      return this.performanceMonitor.getAverageFPS()
    }
    return this.targetFPS
  }

  /**
   * 获取当前降级级别
   */
  getDegradeLevel(): DegradeLevel {
    if (this.performanceMonitor) {
      return this.performanceMonitor.getDegradeLevel()
    }
    return 'none'
  }

  /**
   * 获取性能监控状态
   */
  getPerformanceState(): PerformanceMonitorState | null {
    if (this.performanceMonitor) {
      return this.performanceMonitor.getState()
    }
    return null
  }

  /**
   * 获取建议的目标FPS（基于降级级别）
   */
  getSuggestedTargetFPS(): number {
    if (this.performanceMonitor) {
      return this.performanceMonitor.getSuggestedTargetFPS()
    }
    return this.targetFPS
  }

  /**
   * 是否正在降级
   */
  isDegrading(): boolean {
    if (this.performanceMonitor) {
      return this.performanceMonitor.shouldDegrade()
    }
    return false
  }

  /**
   * 手动设置降级级别
   */
  setDegradeLevel(level: DegradeLevel): void {
    if (this.performanceMonitor) {
      this.performanceMonitor.setDegradeLevel(level)
    }
  }

  /**
   * 重置性能监控
   */
  resetPerformanceMonitor(): void {
    if (this.performanceMonitor) {
      this.performanceMonitor.reset()
    }
  }

  /**
   * 销毁动画循环
   */
  destroy(): void {
    this.stop()
    this.onFrame = () => {}
    this.onPause = undefined
    this.onResume = undefined
    this.onDegrade = undefined
    this.onRecover = undefined
    // Wave 8.2: 销毁性能监控
    if (this.performanceMonitor) {
      this.performanceMonitor.destroy()
      this.performanceMonitor = null
    }
  }
}

/**
 * 创建动画循环的工厂函数
 */
export function createAnimationLoop(
  onFrame: (frame: AnimationFrame) => void,
  targetFPS?: number
): AnimationLoop {
  return new AnimationLoop({
    onFrame,
    targetFPS
  })
}